import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { accountDeletionModule, patientAccountDeletionModule } from '../../lib/api/modules/accountDeletion';
import { deletionStatusKey, formatDay, type DeletionAudience } from './DeleteAccountSection';

/** Whole days until the deletion completes (0 on the day itself). */
export function daysLeft(scheduledFor: string, now: Date = new Date()): number {
  const t = Date.parse(scheduledFor);
  return Number.isNaN(t) ? 0 : Math.max(0, Math.ceil((t - now.getTime()) / 86_400_000));
}

/**
 * Signed in while a deletion is scheduled (the grace period): before anything else, offer "Keep my account" (cancels
 * the deletion and puts public pages back) or "Sign out" (leaves it scheduled). Renders nothing otherwise.
 */
export function PendingDeletionDialog({ audience, onSignOut }: { audience: DeletionAudience; onSignOut: () => void }) {
  const { t, i18n } = useTranslation('auth');
  const qc = useQueryClient();
  const api = audience === 'staff' ? accountDeletionModule : patientAccountDeletionModule;
  const status = useQuery({
    queryKey: deletionStatusKey(audience),
    queryFn: async () => (await api.status()).data ?? null,
    staleTime: 60_000,
  });
  const keep = useMutation({
    mutationFn: async () => {
      const r = await api.cancel();
      if (r.error) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast.success(t('accountDeletion.keptToast'));
      void qc.invalidateQueries({ queryKey: deletionStatusKey(audience) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t('accountDeletion.errors.generic')),
  });

  const pending = status.data?.pending;
  if (!pending) return null;
  const count = daysLeft(pending.scheduledFor);

  return (
    <Dialog open maxWidth="xs" fullWidth aria-labelledby="pending-deletion-title" disableEscapeKeyDown>
      <DialogTitle id="pending-deletion-title">{t('accountDeletion.pending.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.5 }}>
          {t('accountDeletion.pending.body', { date: formatDay(pending.scheduledFor, i18n.language), count })}
        </DialogContentText>
        <DialogContentText variant="body2">{t('accountDeletion.pending.keepHint')}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onSignOut}>{t('accountDeletion.pending.signOut')}</Button>
        <Button variant="contained" onClick={() => keep.mutate()} disabled={keep.isPending}>{t('accountDeletion.pending.keep')}</Button>
      </DialogActions>
    </Dialog>
  );
}
